import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { DesktopHeader } from './DesktopHeader';
import { SideRail } from './SideRail';
import { useProfiles } from '@/modules/profiles/useProfiles';

interface PageShellProps {
  children: ReactNode;
  /**
   * `bare` — landing / onboarding only. No header, no rail, full bleed.
   * Screen System §6: the chassis is incomplete until you are in.
   */
  bare?: boolean;
  /**
   * Page-owned tools for the kit header's utility slot (filters, compose).
   * Not destinations — those live on the Orb.
   */
  mobileTopBar?: ReactNode;
  /**
   * Show the right side rail on lg+ screens. Defaults to true for in-app screens.
   */
  rail?: boolean;
  /**
   * Override the default page padding wrapper class on the main column.
   */
  className?: string;
}

/**
 * In-app chrome around a page.
 *
 * Persistent navigation is not here. The Orb (OrbFan, mounted from App) is
 * the only persistent nav. This shell draws:
 *   - one kit AppHeader — identity + utilities, empty destination items
 *   - optional editorial SideRail on lg+ (HEAT, not destinations)
 *
 * No tab bar. No hamburger. No second header.
 */
export const PageShell = ({
  children,
  bare = false,
  mobileTopBar,
  rail = true,
  className,
}: PageShellProps) => {
  if (bare) {
    return <div className="h-dvh overflow-hidden bg-ink text-text-primary">{children}</div>;
  }

  const { profiles } = useProfiles();
  const liveCount = profiles.filter((p) => p.presence === 'live').length;

  return (
    <div className="flex min-h-dvh flex-col bg-ink text-text-primary">
      <DesktopHeader liveCount={liveCount} extraUtility={mobileTopBar} />

      <div className="flex flex-1">
        <main className={cn('min-w-0 flex-1', className)}>{children}</main>
        {rail && <SideRail />}
      </div>
    </div>
  );
};
